import React, {FC} from 'react';
import {AuctionSeriesViewModelType} from '@joyrideautos/ui-models/src/presentation/auctionSeries/AuctionSeriesViewModel';
import {FixedHeaderRow} from '@components/FixedHeaderRow';
import {Link} from '@joyrideautos/web-common-components/src/router/Router';
import {RightCircleFilled, StopOutlined} from '@ant-design/icons';
import {Typography, List} from 'antd';
import {Pagination} from './Pagination';
import {observer} from 'mobx-react-lite';
import {Routes} from '../../../Routes';
import {AuctionSeriesType} from '@joyrideautos/ui-models/src/types/auction/AuctionSeries';
import {AuctionsGrid} from './AuctionsGrid';
import {Favorite, FavoriteProps} from '@joyrideautos/web-common-components/src/components/favorite/Favorite';
import RequestAccessLink from '@joyrideautos/web-common-components/src/components/requestAccess/RequestAccessLink';
import {AuctionCard} from '@pageComponents/auctionCard/AuctionCard';
import {Only} from '@joyrideautos/ui-common/src/components/Only';
import LiveTag from '@joyrideautos/web-common-components/src/components/LiveTag';

const {Title} = Typography;

interface HeaderTitleProps extends FavoriteProps {
    series: AuctionSeriesType;
    isLive: boolean;
    liveTagText: string;
    isRestricted: boolean;
    isAuthenticated: boolean;
}

const HeaderTitle: FC<HeaderTitleProps> = observer(
    ({series, isLive, liveTagText, isRestricted, isFavorite, toggleFavorite, isAuthenticated}) => {
        const linkToSeries = Routes.AUCTION_SERIES({pathParams: {seriesId: series.id, regionId: series.regionId}});
        return (
            <>
                <List itemLayout={'horizontal'} className="list horizontal list-desc" split={false}>
                    <List.Item className="item">
                        <Title className="title-upcoming" level={3}>
                            <Link
                                data-testid={`auction_series_page_auction_link_series_id_${series.id}`}
                                to={linkToSeries}>
                                {series.title}
                            </Link>
                        </Title>
                    </List.Item>
                    {isLive && (
                        <List.Item className="item">
                            <LiveTag text={liveTagText} />
                        </List.Item>
                    )}
                    {isRestricted && (
                        <List.Item className="item">
                            <span className="orange">
                                <StopOutlined /> Restricted
                            </span>
                        </List.Item>
                    )}
                    <List.Item className="item">
                        <Title className="title-upcoming" level={3}>
                            <Link to={linkToSeries}>
                                <RightCircleFilled className="disabled" />
                            </Link>
                        </Title>
                    </List.Item>
                    {isAuthenticated && (
                        <List.Item className="item">
                            <Favorite isFavorite={isFavorite} toggleFavorite={toggleFavorite} className="no-circle" />
                        </List.Item>
                    )}
                </List>
            </>
        );
    }
);

interface AuctionSerieViewProps {
    model: AuctionSeriesViewModelType;
    isAuthenticated: boolean;
}

const AuctionSeries: FC<AuctionSerieViewProps> = observer(({model, isAuthenticated}) => {
    return (
        <div className="auction-serie-row">
            <section className="section-upcoming">
                <FixedHeaderRow
                    className="section-filter"
                    left={
                        <HeaderTitle
                            series={model.series}
                            isLive={model.isLive}
                            liveTagText={model.liveAuctionTagText}
                            isRestricted={model.isRestricted}
                            isFavorite={model.isFavorite}
                            toggleFavorite={() => model.toggleFavorite()}
                            isAuthenticated={isAuthenticated}
                        />
                    }
                    right={
                        <Pagination
                            model={model}
                            hidePages={true}
                            action={
                                <Only when={model.isRequestAccessButtonVisible}>
                                    <RequestAccessLink />
                                </Only>
                            }
                        />
                    }
                />
            </section>
            <section className="section-card">
                <AuctionsGrid
                    auctions={model.auctions}
                    render={(auction) => (
                        <AuctionCard model={model.getAuctionCardModel(auction)} isAuthenticated={isAuthenticated} />
                    )}
                />
            </section>
        </div>
    );
});

export default AuctionSeries;
